/**
 * One register, across the whole portfolio.
 *
 * The eight registers in the rail used to navigate to the projects list. All eight of them,
 * to the same place — so clicking "Payments" and clicking "Risks" did the same thing, and
 * from the projects screen they did nothing observable at all. That is how it was reported:
 * "when i click on registers nothing works".
 *
 * The endpoint for this existed the whole time. `GET /risks` lists the register across every
 * package with the same paging, filtering, sorting and search as `GET /packages/{id}/risks`;
 * only the package-scoped one had ever been given a screen.
 *
 * And it is the more valuable of the two. "Every risk scored 15 or above, anywhere in the
 * portfolio" is the question a director asks and the one the product could not answer — you
 * had to open 517 packages to assemble it by hand. The grid's filters and totals are exactly
 * what make 1,291 rows answerable rather than merely present.
 *
 * Editing is deliberately not offered here. A cell edit raises an application against a
 * package, and a portfolio-wide grid is a reading surface: you find the row here and open its
 * package to act on it. Offering an edit that then asked "which package?" would be worse than
 * not offering one.
 */

import { useQuery } from '@tanstack/react-query'
import { useTranslation } from 'react-i18next'

import { api, ApiError } from '@/api/client'
import type { RegisterInfo } from '@/api/types'
import { Failed, Skeleton } from '@/components/states'
import { RegisterGrid } from '@/features/registers/RegisterGrid'
import { registerLabel } from '@/lib/registers'

export function PortfolioRegister({ slug }: { slug: string }) {
  const { t } = useTranslation()

  const registers = useQuery({
    queryKey: ['registers'],
    queryFn: ({ signal }) => api.get<RegisterInfo[]>('/registers', signal),
    staleTime: Infinity,
  })

  if (registers.isError) {
    const error = registers.error
    return (
      <main className="page">
        <Failed
          message={error instanceof ApiError ? error.friendly : String(error)}
          onRetry={() => void registers.refetch()}
        />
      </main>
    )
  }

  if (registers.isPending) {
    return (
      <main className="page">
        <Skeleton rows={6} />
      </main>
    )
  }

  const register = registers.data.find((entry) => entry.slug === slug)

  if (!register) {
    // A slug that is not a register. Says which ones are, rather than rendering an empty
    // grid that looks like a register with nothing in it.
    return (
      <main className="page" data-testid="portfolio-register">
        <header className="page__header">
          <div>
            <h1 className="page__title">{t('register.unknownTitle')}</h1>
            <p className="page__subtitle">
              {t('register.unknownHint', {
                registers: registers.data
                  .map((entry) => registerLabel(t, entry.slug, entry.label))
                  .join(', '),
              })}
            </p>
          </div>
        </header>
      </main>
    )
  }

  const label = registerLabel(t, register.slug, register.label)

  return (
    <main className="page" data-testid="portfolio-register">
      <header className="page__header">
        <div>
          <p className="page__eyebrow">{t('nav.registers')}</p>
          <h1 className="page__title">{label}</h1>
          <p className="page__subtitle">{t('register.portfolioSubtitle')}</p>
        </div>
      </header>

      <RegisterGrid
        register={register}
        packageId={null}
        // Read-only across the portfolio: an edit belongs to a package, and this grid does
        // not have one. The row's reference still opens its history, which is the thing
        // somebody wants from here.
        onPropose={() => undefined}
        onUnlock={() => undefined}
      />
    </main>
  )
}
